import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { canDo } from "../../utils/routeAccess";
import { useImmer } from "use-immer";
import cx from "classnames";
import { client } from "../../services/axiosClient";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";

// hooks
import useThemeColors from "../../hooks/useThemeColors";

// material-ui icons
import {
  AccountBalance,
  Add,
  Delete,
  Save,
  MoreVert,
  ExpandMore,
  ExpandLess,
  RadioButtonUnchecked,
  RadioButtonChecked,
} from "@material-ui/icons";

import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
library.add(faCheck);

// core components
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardHeader from "../../components/Card/CardHeader.js";
import { ThreeDots } from "react-loader-spinner";

import exchangeRatesStyle from "../../assets/jss/material-dashboard-pro-react/views/exchangeRatesStyle";

import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import CustomInput from "../../components/CustomInput/CustomInput";
import Button from "../../components/CustomButtons/Button";
import ModalHeader from "../../components/ModalHeader";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Menu,
  IconButton,
  Collapse,
  Divider,
  ListSubheader,
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { toast } from "react-toastify";
import { Slide as ToastSlide } from "react-toastify";
import SnackbarContent from "../../components/Snackbar/SnackbarContent";

export default function ExchangeRates() {
  const { clientColor } = useThemeColors();

  const useStyles = makeStyles(exchangeRatesStyle);
  const classes = useStyles();

  const { t, i18n } = useTranslation();

  // Capacidade de editar taxas: atomo no sistema novo; no legado, qualquer
  // role que enxerga a pagina (paridade com o gate de rota legado).
  const userDetails = useSelector((state) => state.auth.userDetails);
  const canEditRates = canDo(userDetails, "modeling.exchange_rates.edit", [
    "DEV",
    "IMG",
    "CAD",
    "CST",
    "CFA",
    "CIF",
    "CIP",
  ]);

  const [dataLoaded, setDataLoaded] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [exchangeRatesData, setExchangeRatesData] = useImmer({});

  const [addCurrencyDialogOpen, setAddCurrencyDialogOpen] = useState(false);
  const [currencyToAdd, setCurrencyToAdd] = useState("");
  const [removeCurrencyDialogOpen, setRemoveCurrencyDialogOpen] =
    useState(false);
  const [currencyToRemove, setCurrencyToRemove] = useState("");
  const [saving, setSaving] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuCurrency, setMenuCurrency] = useState("");
  const [instructionsOpen, setInstructionsOpen] = useState(true);

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const convertRatesToDisplay = (data) => {
    if (!data.rates || !data.currency_directions) return data;
    const convertedRates = {};
    for (const currency of Object.keys(data.rates)) {
      convertedRates[currency] = {};
      const direction = data.currency_directions[currency] || "base_to_target";
      for (const month of Object.keys(data.rates[currency])) {
        const rate = data.rates[currency][month];
        if (rate != null) {
          let displayRate =
            direction === "target_to_base" && rate !== 0 ? 1 / rate : rate;
          convertedRates[currency][month] = parseFloat(displayRate.toFixed(7));
        } else {
          convertedRates[currency][month] = rate;
        }
      }
    }
    return { ...data, rates: convertedRates };
  };

  useEffect(() => {
    client
      .post("api/exchange_rates_get_current_data/")
      .then((response) => {
        setExchangeRatesData(convertRatesToDisplay(response.data));
        setDataLoaded(true);
      })
      .catch((error) => {
        notify(
          t("modeling_exchange_rates." + error.response.data.error_code),
          "error",
        );
        setDataLoaded(true);
        console.log(error);
      });
  }, []);

  const notify = (message, type = "info") =>
    toast(message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });

  const loadingSpinner = () => {
    return (
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <ThreeDots
          width="100px"
          height="100px"
          color={clientColor}
          ariaLabel="loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      </div>
    );
  };

  const handleRateBlur = (e, currency, month) => {
    let val = e.target.value;
    val = parseFloat(val);
    if (isNaN(val)) {
      val = null;
    } else {
      if (val <= 0) {
        val = null;
      }
    }
    if (val !== null) {
      val = parseFloat(val.toFixed(7));
    }
    let change;
    if (exchangeRatesData.rates[currency][month]) {
      change = val !== exchangeRatesData.rates[currency][month];
    } else {
      change = val !== null;
    }
    if (change) {
      setExchangeRatesData((draft) => {
        draft.rates[currency][month] = val;
      });
      setSavePending(true);
    }
  };
  const handleRateChange = (e, currency, month) => {
    setExchangeRatesData((draft) => {
      draft["rates"][currency][month] = e.target.value;
    });
    setSavePending(true);
  };
  const handleAddCurrencyButtonClick = (e) => {
    e.target.blur();
    setCurrencyToAdd("");
    setAddCurrencyDialogOpen(true);
  };
  const handleCurrencyToAddSelect = (e) => {
    setCurrencyToAdd(e.target.value);
  };
  const getFilteredAvailableCurrencies = () => {
    if (!exchangeRatesData.available_currencies) return [];
    return exchangeRatesData.available_currencies.filter(
      (c) =>
        !exchangeRatesData.currencies.includes(c.iso) &&
        c.iso !== exchangeRatesData.standard_currency,
    );
  };
  const getCurrencySymbol = (currencyIso) => {
    if (!exchangeRatesData.available_currencies) return "";
    const found = exchangeRatesData.available_currencies.find(
      (c) => c.iso === currencyIso,
    );
    return found ? found.symbol : "";
  };
  const getCurrencyDirection = (currencyIso) => {
    return (
      exchangeRatesData.currency_directions?.[currencyIso] || "base_to_target"
    );
  };
  const toDisplayRate = (rate, currency) => {
    if (rate == null) return null;
    const direction = getCurrencyDirection(currency);
    if (direction === "target_to_base") {
      return rate !== 0 ? 1 / rate : null;
    }
    return rate;
  };
  const toStorageRate = (rate, currency) => {
    if (rate == null) return null;
    const direction = getCurrencyDirection(currency);
    if (direction === "target_to_base") {
      return rate !== 0 ? 1 / rate : null;
    }
    return rate;
  };
  const handleCurrencyAddConfirm = () => {
    if (
      exchangeRatesData.currencies.includes(currencyToAdd) ||
      exchangeRatesData.standard_currency === currencyToAdd ||
      exchangeRatesData.currencies.length >= 8
    )
      return false;

    const currency_to_add = currencyToAdd;
    setCurrencyToAdd("");
    setAddCurrencyDialogOpen(false);
    setSaving(true);

    const newCurrencies = [...exchangeRatesData.currencies, currency_to_add];
    const newDirections = {
      ...(exchangeRatesData.currency_directions || {}),
      [currency_to_add]: "base_to_target",
    };

    client
      .post("api/exchange_rates_save/", {
        currencies: newCurrencies,
        currency_directions: newDirections,
        rates: exchangeRatesData.rates,
      })
      .then(() => {
        return client.post("api/exchange_rates_get_current_data/");
      })
      .then((response) => {
        setExchangeRatesData(convertRatesToDisplay(response.data));
        notify(t("modeling_exchange_rates.saved_successfully"));
        setSaving(false);
        setSavePending(false);
      })
      .catch((error) => {
        notify(
          t(
            "modeling_exchange_rates." +
              (error.response?.data?.error_code ||
                "error_saving_exchange_rates"),
          ),
          "error",
        );
        setSaving(false);
      });
  };
  const handleRemoveCurrencyDirect = (currency) => {
    setCurrencyToRemove(currency);
    setRemoveCurrencyDialogOpen(true);
  };
  const handleMenuOpen = (event, currency) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuCurrency(currency);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuCurrency("");
  };

  const handleMenuDeleteClick = () => {
    handleMenuClose();
    handleRemoveCurrencyDirect(menuCurrency);
  };
  const handleDirectionChange = (currency, newDirection) => {
    const currentDirection = getCurrencyDirection(currency);
    if (currentDirection === newDirection) {
      handleMenuClose();
      return;
    }
    setExchangeRatesData((draft) => {
      if (!draft.currency_directions) {
        draft.currency_directions = {};
      }
      draft.currency_directions[currency] = newDirection;
      if (draft.rates && draft.rates[currency]) {
        for (const month of Object.keys(draft.rates[currency])) {
          const val = draft.rates[currency][month];
          if (val != null && val !== "" && val !== 0) {
            const numVal = parseFloat(val);
            if (!isNaN(numVal) && numVal !== 0) {
              const inverted = 1 / numVal;
              draft.rates[currency][month] =
                Math.round(inverted * 10000000) / 10000000;
            }
          }
        }
      }
    });
    setSavePending(true);
    handleMenuClose();
  };

  const handleRemoveCurrencyConfirmAndSave = () => {
    if (saving) return false;
    const currency = currencyToRemove;
    const index = exchangeRatesData.currencies.indexOf(currency);
    const newCurrencies = [...exchangeRatesData.currencies];
    newCurrencies.splice(index, 1);
    const newRates = { ...exchangeRatesData.rates };
    delete newRates[currency];
    const newDirections = { ...(exchangeRatesData.currency_directions || {}) };
    delete newDirections[currency];

    const params = {
      currencies: newCurrencies,
      currency_directions: newDirections,
      rates: newRates,
    };

    setSaving(true);
    setRemoveCurrencyDialogOpen(false);
    client
      .post("api/exchange_rates_save/", params)
      .then(() => {
        setExchangeRatesData((draft) => {
          draft.currencies.splice(index, 1);
          delete draft.rates[currency];
        });
        notify(t("modeling_exchange_rates.currency_removed_successfully"));
        setSaving(false);
        setCurrencyToRemove("");
      })
      .catch((error) => {
        notify(
          t("modeling_exchange_rates." + error.response.data.error_code),
          "error",
        );
        setSaving(false);
        setCurrencyToRemove("");
        console.log(error);
      });
  };
  const handleSaveCurrencyDirect = () => {
    if (saving) return false;
    const ratesToSave = {};
    for (const currency of exchangeRatesData.currencies) {
      ratesToSave[currency] = {};
      for (const month of exchangeRatesData.months) {
        const displayValue = exchangeRatesData.rates[currency]?.[month];
        const parsedValue = parseFloat(displayValue);
        if (!isNaN(parsedValue) && parsedValue > 0) {
          ratesToSave[currency][month] = toStorageRate(parsedValue, currency);
        } else {
          ratesToSave[currency][month] = null;
        }
      }
    }
    let params = {
      currencies: exchangeRatesData.currencies,
      currency_directions: exchangeRatesData.currency_directions || {},
      rates: ratesToSave,
    };
    setSaving(true);
    setSavePending(false);
    client
      .post("api/exchange_rates_save/", params)
      .then(() => {
        notify(t("modeling_exchange_rates.saved_successfully"));
        setSaving(false);
      })
      .catch((error) => {
        notify(
          t("modeling_exchange_rates." + error.response.data.error_code),
          "error",
        );
        setSaving(false);
        setSavePending(true);
        console.log(error);
      });
  };

  const addCurrencyDialog = () => {
    return (
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal,
        }}
        open={addCurrencyDialogOpen}
        keepMounted
        onClose={() => setAddCurrencyDialogOpen(false)}
        aria-labelledby="classic-modal-slide-title"
        aria-describedby="classic-modal-slide-description"
        fullWidth
        maxWidth={"xs"}
      >
        <ModalHeader
          id="content-modal-slide-title"
          title={t("modeling_exchange_rates.add_currency")}
          onClose={() => setAddCurrencyDialogOpen(false)}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent
          id="classic-modal-slide-description"
          className={classes.modalBody}
        >
          <FormControl fullWidth className={classes.selectFormControl}>
            <InputLabel
              htmlFor="currency-add-select"
              className={classes.selectLabel}
            >
              {t("modeling_exchange_rates.select_currency_to_add")}
            </InputLabel>
            <Select
              MenuProps={{
                className: classes.selectMenu,
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              classes={{
                select: classes.select,
              }}
              value={currencyToAdd}
              onChange={handleCurrencyToAddSelect}
              inputProps={{
                name: "currencyAddSelect",
                id: "currency-add-select",
              }}
            >
              {getFilteredAvailableCurrencies().map((currency) => (
                <MenuItem
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                  value={currency.iso}
                  key={currency.iso}
                >
                  {currency.symbol} {currency.iso} - {currency.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          <Button
            onClick={() => handleCurrencyAddConfirm()}
            color="info"
            simple
            disabled={currencyToAdd === ""}
          >
            {t("modeling_exchange_rates.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  const removeCurrencyDialog = () => {
    return (
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal,
        }}
        open={removeCurrencyDialogOpen}
        keepMounted
        onClose={() => setRemoveCurrencyDialogOpen(false)}
        aria-labelledby="classic-modal-slide-title"
        aria-describedby="classic-modal-slide-description"
        fullWidth
        maxWidth={"xs"}
      >
        <ModalHeader
          id="content-modal-slide-title"
          title={t("modeling_exchange_rates.remove_currency")}
          pills={[
            {
              key: "permanent",
              label: t("common.permanent"),
              tone: "error",
              dot: true,
            },
          ]}
          onClose={() => setRemoveCurrencyDialogOpen(false)}
          closeLabel={t("common.close")}
          clientColor={clientColor}
        />
        <DialogContent
          id="classic-modal-slide-description"
          className={classes.modalBody}
        >
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            {t("modeling_exchange_rates.confirm_remove_currency")}
            <strong style={{ marginLeft: "4px" }}>{currencyToRemove}</strong>?
          </div>
          <div className={classes.removeCurrencyWarning}>
            {t("modeling_exchange_rates.remove_currency_warning_permanent")}
          </div>
        </DialogContent>
        <DialogActions className={classes.modalFooter}>
          <Button
            onClick={() => setRemoveCurrencyDialogOpen(false)}
            color="transparent"
            simple
          >
            {t("modeling_exchange_rates.cancel")}
          </Button>
          <Button
            onClick={() => handleRemoveCurrencyConfirmAndSave()}
            color="danger"
            simple
            disabled={saving}
          >
            {t("modeling_exchange_rates.confirm_delete")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div>
      <GridContainer style={{ marginTop: "-40px" }}>
        <GridItem xs={12}>
          <div>
            <Card>
              <CardHeader color="client" icon>
                <CardIcon color="client">
                  <AccountBalance />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  {t("modeling_exchange_rates.exchange_rates")}
                </h4>
              </CardHeader>
              <CardBody className={classes.cardBody}>
                <GridContainer justifyContent="flex-start">
                  {dataLoaded && Object.keys(exchangeRatesData).length > 0 && (
                    <>
                      {/* Instruções */}
                      <div className={classes.instructionsWrapper}>
                        <div
                          className={classes.instructionsHeader}
                          onClick={() => setInstructionsOpen(!instructionsOpen)}
                        >
                          <span className={classes.instructionsTitle}>
                            {t("modeling_exchange_rates.instructions")}
                          </span>
                          {instructionsOpen ? <ExpandLess /> : <ExpandMore />}
                        </div>
                        <Collapse in={instructionsOpen}>
                          <div className={classes.comments}>
                            {[...Array(4).keys()].map((key, val) => {
                              return (
                                <div key={key}>
                                  {"• "}
                                  {t(
                                    "modeling_exchange_rates.comments.comment" +
                                      (val + 1),
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </Collapse>
                      </div>
                      {/* Moeda padrão */}
                      <div
                        className={cx({
                          [classes.topInformation]: true,
                          [classes.standardCurrency]: true,
                        })}
                      >
                        {t("modeling_exchange_rates.standard_currency") + ": "}
                        <strong>{exchangeRatesData.standard_currency}</strong>
                      </div>
                      {/* Moedas adicionais */}
                      <div
                        className={cx({
                          [classes.topInformation]: true,
                          [classes.additionalCurrencies]: true,
                        })}
                      >
                        {t("modeling_exchange_rates.additional_currencies")}
                        {saving && (
                          <ThreeDots
                            width="30px"
                            height="10px"
                            color={clientColor}
                            ariaLabel="loading"
                            wrapperStyle={{
                              display: "inline-block",
                              marginLeft: "10px",
                            }}
                            wrapperClass=""
                          />
                        )}
                      </div>
                      {/* table */}
                      <div className={classes.tableWrapper}>
                        <div
                          className={cx({
                            [classes.currencyTable]: true,
                          })}
                        >
                          {/* header */}
                          <div
                            className={cx({
                              [classes.currencyTableHeader]: true,
                              [classes.currencyTableRow]: true,
                            })}
                          >
                            <div
                              className={cx({
                                [classes.currencyTableColMonth]: true,
                              })}
                            >
                              {t("modeling_exchange_rates.month")}
                            </div>
                            {exchangeRatesData.currencies.map(
                              (currency, key) => {
                                return (
                                  <div
                                    key={key}
                                    className={cx({
                                      [classes.currencyTableColCurrency]: true,
                                      [classes.currencyTableColHeader]: true,
                                    })}
                                  >
                                    <div className={classes.currencyHeaderRow}>
                                      <span className={classes.currencyName}>
                                        {currency}{" "}
                                        {getCurrencySymbol(currency) &&
                                          `(${getCurrencySymbol(currency)})`}
                                      </span>
                                      <IconButton
                                        size="small"
                                        onClick={(e) =>
                                          handleMenuOpen(e, currency)
                                        }
                                        disabled={saving}
                                        className={classes.moreButton}
                                      >
                                        <MoreVert fontSize="small" />
                                      </IconButton>
                                    </div>
                                    <span className={classes.conversionLabel}>
                                      {getCurrencyDirection(currency) ===
                                      "base_to_target"
                                        ? `${exchangeRatesData.standard_currency} → ${currency}`
                                        : `${currency} → ${exchangeRatesData.standard_currency}`}
                                    </span>
                                  </div>
                                );
                              },
                            )}
                          </div>
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left:
                                108 + exchangeRatesData.currencies.length * 222,
                              display: "flex",
                              gap: "8px",
                            }}
                          >
                            <Button
                              aria-label="save"
                              color={savePending ? "warning" : "client"}
                              size={"sm"}
                              onClick={() => handleSaveCurrencyDirect()}
                              disabled={!savePending || saving || !canEditRates}
                              className={cx({
                                [classes.attention]: savePending,
                                [classes.saveButton]: true,
                              })}
                            >
                              <Save />
                              {t("modeling_exchange_rates.save")}
                            </Button>
                            {exchangeRatesData.currencies.length < 8 && (
                              <Button
                                aria-label="add_currency"
                                color="client"
                                size={"sm"}
                                onClick={(e) => handleAddCurrencyButtonClick(e)}
                                disabled={saving}
                              >
                                <Add />
                                {t("modeling_exchange_rates.add_currency")}
                              </Button>
                            )}
                          </div>
                          {/* body */}
                          {exchangeRatesData.months.map((month, key1) => {
                            return (
                              <div
                                key={key1}
                                className={cx({
                                  [classes.currencyTableRow]: true,
                                })}
                              >
                                <div
                                  className={cx({
                                    [classes.currencyTableBodyCol]: true,
                                    [classes.currencyTableColMonth]: true,
                                  })}
                                >
                                  {month}
                                </div>
                                {exchangeRatesData.currencies.map(
                                  (currency, key2) => {
                                    const rawReferenceRate =
                                      exchangeRatesData.reference_rates?.[
                                        currency
                                      ]?.[month];
                                    const referenceRate = toDisplayRate(
                                      rawReferenceRate,
                                      currency,
                                    );
                                    const manualVal =
                                      exchangeRatesData.rates[currency][month];
                                    const hasManualValue =
                                      manualVal != null && manualVal !== "";
                                    return (
                                      <div
                                        key={key2}
                                        className={cx({
                                          [classes.currencyTableBodyCol]: true,
                                          [classes.currencyTableColCurrency]: true,
                                          [classes.currencyTableCellWithRef]: true,
                                        })}
                                      >
                                        <span
                                          className={
                                            hasManualValue
                                              ? classes.referenceRateDimmed
                                              : classes.referenceRate
                                          }
                                        >
                                          {referenceRate != null
                                            ? referenceRate.toFixed(7)
                                            : "n.a."}
                                        </span>
                                        <CustomInput
                                          id="currency_value"
                                          value={
                                            manualVal != null &&
                                            manualVal !== ""
                                              ? manualVal.toString()
                                              : ""
                                          }
                                          onChange={(e) =>
                                            handleRateChange(e, currency, month)
                                          }
                                          onBlur={(e) =>
                                            handleRateBlur(e, currency, month)
                                          }
                                          formControlProps={{
                                            style: {
                                              paddingTop: "0",
                                              paddingBottom: "0",
                                              marginTop: "-2px",
                                              marginBottom: "0",
                                              width: "100px",
                                              minWidth: "24px",
                                            },
                                            className: classes.inputBold,
                                          }}
                                          inputProps={{
                                            type: "number",
                                            autoComplete: "off",
                                            disabled: false,
                                          }}
                                          client
                                        />
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                  {!dataLoaded && loadingSpinner()}
                  {dataLoaded &&
                    Object.keys(exchangeRatesData).length === 0 && (
                      <SnackbarContent
                        message={t("modeling_exchange_rates.error_on_loading")}
                        color="danger"
                        paperClass={classes.snackbarPaper}
                        messageClass={classes.snackbarMessage}
                      />
                    )}
                </GridContainer>
              </CardBody>
            </Card>
          </div>
        </GridItem>
      </GridContainer>
      {addCurrencyDialog()}
      {removeCurrencyDialog()}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <ListSubheader style={{ lineHeight: "32px" }}>
          {t("modeling_exchange_rates.conversion_order")}
        </ListSubheader>
        <MenuItem
          onClick={() => handleDirectionChange(menuCurrency, "base_to_target")}
        >
          {getCurrencyDirection(menuCurrency) === "base_to_target" ? (
            <RadioButtonChecked fontSize="small" style={{ marginRight: 8 }} />
          ) : (
            <RadioButtonUnchecked fontSize="small" style={{ marginRight: 8 }} />
          )}
          {exchangeRatesData.standard_currency} → {menuCurrency}
        </MenuItem>
        <MenuItem
          onClick={() => handleDirectionChange(menuCurrency, "target_to_base")}
        >
          {getCurrencyDirection(menuCurrency) === "target_to_base" ? (
            <RadioButtonChecked fontSize="small" style={{ marginRight: 8 }} />
          ) : (
            <RadioButtonUnchecked fontSize="small" style={{ marginRight: 8 }} />
          )}
          {menuCurrency} → {exchangeRatesData.standard_currency}
        </MenuItem>
        <Divider style={{ margin: "8px 0" }} />
        <MenuItem onClick={handleMenuDeleteClick}>
          <Delete fontSize="small" style={{ marginRight: 8 }} />
          {t("modeling_exchange_rates.delete_currency")}
        </MenuItem>
      </Menu>
    </div>
  );
}
